import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { MapPin, DollarSign, Briefcase, Clock, Bookmark, Send, ChevronLeft } from "lucide-react";
import { TID } from "@/constants/testIds";

export default function JobDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [coverNote, setCoverNote] = useState("");
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    api.get(`/jobs/${id}`).then(r => setJob(r.data)).catch(() => toast.error("Job not found"));
  }, [id]);

  const apply = async () => {
    if (!user) return nav("/login");
    if (user.role !== "candidate") return toast.error("Only job seekers can apply. Please register as a candidate.");
    setApplying(true);
    try {
      await api.post("/applications", { job_id: id, cover_note: coverNote });
      toast.success("Application submitted");
      nav("/dashboard/candidate/applications");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not apply");
    } finally {
      setApplying(false);
    }
  };

  const save = async () => {
    if (!user) return nav("/login");
    if (user.role !== "candidate") return toast.error("Only job seekers can save jobs.");
    try {
      await api.post(`/jobs/${id}/save`);
      toast.success("Saved to your list");
    } catch { toast.error("Save failed"); }
  };

  if (!job) return <div className="section container-ax text-slate-500">Loading…</div>;

  return (
    <div className="section container-ax max-w-4xl">
      <Link to="/jobs" className="text-sm text-slate-500 hover:text-navy inline-flex items-center gap-1 mb-6">
        <ChevronLeft className="w-4 h-4" /> All jobs
      </Link>
      <div className="eyebrow mb-2">{job.industry}</div>
      <h1 className="font-display text-3xl md:text-4xl font-bold text-navy">{job.title}</h1>
      <div className="text-slate-600 mt-2">{job.employer_name}</div>

      <div className="mt-6 flex flex-wrap gap-4 text-sm text-slate-600">
        {job.city && <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" /> {job.city}{job.state ? `, ${job.state}` : ""}</span>}
        <span className="inline-flex items-center gap-1"><Briefcase className="w-4 h-4" /> {job.employment_type}</span>
        <span className="inline-flex items-center gap-1"><Clock className="w-4 h-4" /> {job.work_arrangement}</span>
        {(job.salary_min || job.salary_max) && (
          <span className="inline-flex items-center gap-1"><DollarSign className="w-4 h-4" /> {job.salary_min}{job.salary_max ? `–${job.salary_max}` : ""} / {job.pay_type}</span>
        )}
      </div>

      <div className="mt-8 space-y-6 text-slate-700 leading-relaxed">
        <div>
          <div className="eyebrow mb-2">Overview</div>
          <p className="whitespace-pre-wrap">{job.description}</p>
        </div>
        {job.requirements && (
          <div>
            <div className="eyebrow mb-2">Required Qualifications</div>
            <p className="whitespace-pre-wrap">{job.requirements}</p>
          </div>
        )}
        {job.preferred && (
          <div>
            <div className="eyebrow mb-2">Preferred Qualifications</div>
            <p className="whitespace-pre-wrap">{job.preferred}</p>
          </div>
        )}
        {job.benefits && (
          <div>
            <div className="eyebrow mb-2">Benefits</div>
            <p className="whitespace-pre-wrap">{job.benefits}</p>
          </div>
        )}
        {job.schedule && (
          <div>
            <div className="eyebrow mb-2">Schedule</div>
            <p>{job.schedule} {job.shift ? `• ${job.shift} shift` : ""}</p>
          </div>
        )}
      </div>

      {/* Apply / Save panel */}
      <div className="mt-10 border border-slate-200 rounded-xl p-6 bg-white">
        {user && user.role === "candidate" ? (
          <>
            <div className="eyebrow mb-2">Apply now</div>
            <textarea rows={4} value={coverNote} onChange={e => setCoverNote(e.target.value)}
              placeholder="Add a short note to your recruiter (optional)"
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
            <div className="mt-4 flex flex-wrap gap-3">
              <button onClick={apply} disabled={applying} data-testid={TID.candidateApplyBtn} className="btn-navy">
                <Send className="w-4 h-4" /> {applying ? "Sending…" : "Submit application"}
              </button>
              <button onClick={save} data-testid={TID.candidateSaveBtn}
                className="rounded-full px-5 py-3 border border-slate-200 hover:border-navy text-navy inline-flex items-center gap-2 text-sm font-medium">
                <Bookmark className="w-4 h-4" /> Save for later
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="text-navy font-display text-lg font-semibold">Interested in this role?</div>
            <p className="text-slate-600 text-sm mt-1">Create a candidate account to apply and track your application.</p>
            <div className="mt-4 flex gap-3">
              <Link to="/register?role=candidate" className="btn-navy">Create candidate account</Link>
              <Link to="/login" className="rounded-full px-5 py-3 border border-slate-200 text-navy hover:border-navy text-sm font-medium">Sign in</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
